"use client";

import { useSearchParams } from "@klarify/i18n/navigation";
import { ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";

type Props = {
	directoryLabel: string;
	/** The server-rendered clinic back-link, shown unless `?from=directory`. */
	children: ReactNode;
};

/**
 * Swaps the clinic back-link for a directory one when the visitor arrived via
 * `?from=directory`.
 *
 * Only the override is client-side. Reading the param in the page's
 * `searchParams` would make the whole route dynamic; rendering the whole
 * breadcrumb here would drop an internal link out of the prerendered HTML,
 * since `useSearchParams` client-renders its subtree down to the Suspense
 * boundary. So the default link is server-rendered — passed both as the
 * boundary's fallback and as `children` — and only replaced after hydration.
 */
export function TherapistBreadcrumb({ directoryLabel, children }: Props) {
	if (useSearchParams().get("from") !== "directory") {
		return <>{children}</>;
	}
	return (
		// Plain `<a>`: the /therapists directory lives in apps/marketing —
		// multi-zone soft navigation isn't supported.
		<a
			href="/therapists"
			className="inline-flex items-center gap-1.5 text-klarify-neutral-600 text-sm transition-colors hover:text-klarify-forest-700"
		>
			<ChevronLeft className="h-4 w-4" />
			<span>{directoryLabel}</span>
		</a>
	);
}
TherapistBreadcrumb.displayName = "TherapistBreadcrumb";
